/**
 * Avédon scarcity engine integration.
 *
 * Calls the deployed Cloudflare Worker (avedon-scarcity-engine) for live
 * stock/edition state and pre-launch countdown timing. This is the only
 * file in the theme that knows the Worker's URL — change it here once
 * if the Worker ever moves to a custom domain (e.g. api.avedonco.com).
 */

const AVEDON_SCARCITY_API = 'https://avedon-scarcity-engine.michaelamarino16.workers.dev';

/**
 * Fetches live state for a single variant. Returns null on any failure
 * (network error, 5xx, malformed response) rather than throwing — a
 * scarcity display failure should never block the page from rendering
 * or block someone from adding to cart. Shopify's own variant.available
 * remains the real backstop against overselling regardless of whether
 * this call succeeds.
 */
async function fetchStockState(variantId) {
  try {
    const res = await fetch(`${AVEDON_SCARCITY_API}/api/stock/${variantId}`);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.warn('Avédon scarcity engine unreachable:', err);
    return null;
  }
}

/**
 * Batch fetch for collection/grid pages — one request for many product
 * cards instead of one fetch per card.
 */
async function fetchStockStateBatch(variantIds) {
  try {
    const res = await fetch(`${AVEDON_SCARCITY_API}/api/stock/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantIds }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data.results ?? {};
  } catch (err) {
    console.warn('Avédon scarcity engine batch fetch failed:', err);
    return {};
  }
}

/**
 * Renders the edition-count display into a target element, given a
 * stock state object from the API. Handles all three states (active,
 * low_stock, sold_out) plus the untracked case (no element changes —
 * the product behaves as a normal, non-limited Shopify product).
 */
function renderEditionCount(el, state) {
  if (!state || !state.tracked) return;

  const { stockCount, status } = state;
  el.textContent = `${stockCount} remain`;
  el.classList.remove('low', 'sold');
  if (status === 'low_stock') el.classList.add('low');
  if (status === 'sold_out') {
    el.textContent = 'Sold out';
    el.classList.add('sold');
  }
}

/**
 * Renders a live countdown (days/hours/minutes/seconds) into a
 * container, counting down to launchAt. When the countdown reaches
 * zero, calls onComplete so the caller can swap to the live edition
 * display — this is the mechanism that makes the pre-launch -> live
 * transition automatic, with no manual flip needed.
 */
function renderCountdown(container, launchAtIso, onComplete) {
  const launchTime = new Date(launchAtIso).getTime();

  function tick() {
    const now = Date.now();
    const diff = launchTime - now;

    if (diff <= 0) {
      clearInterval(interval);
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    const unitEls = container.querySelectorAll('[data-unit]');
    unitEls.forEach((unitEl) => {
      const unit = unitEl.dataset.unit;
      const value = { days, hours, minutes, seconds }[unit];
      if (value !== undefined) unitEl.textContent = String(value).padStart(2, '0');
    });
  }

  tick();
  const interval = setInterval(tick, 1000);
}

/**
 * Auto-initializes every scarcity/countdown element on the page.
 * Looks for [data-avedon-variant-id] for single-item displays
 * (product pages) and batches everything else (collection grids).
 */
function initAvedonScarcity() {
  const singleEls = document.querySelectorAll('[data-avedon-variant-id]');
  const gridEls = document.querySelectorAll('[data-avedon-grid-variant-id]');

  singleEls.forEach(async (el) => {
    const variantId = el.dataset.avedonVariantId;
    const state = await fetchStockState(variantId);
    if (!state || !state.tracked) return;

    if (state.dropLaunchAt && new Date(state.dropLaunchAt).getTime() > Date.now()) {
      const countdownEl = el.querySelector('[data-avedon-countdown]');
      const editionEl = el.querySelector('[data-avedon-edition-count]');
      if (countdownEl) {
        countdownEl.style.display = '';
        if (editionEl) editionEl.style.display = 'none';
        renderCountdown(countdownEl, state.dropLaunchAt, () => {
          countdownEl.style.display = 'none';
          if (editionEl) {
            editionEl.style.display = '';
            renderEditionCount(editionEl, state);
          }
        });
        return;
      }
    }

    const editionEl = el.querySelector('[data-avedon-edition-count]');
    if (editionEl) renderEditionCount(editionEl, state);
  });

  if (gridEls.length > 0) {
    const variantIds = Array.from(gridEls).map((el) => el.dataset.avedonGridVariantId);
    fetchStockStateBatch(variantIds).then((results) => {
      gridEls.forEach((el) => {
        const variantId = el.dataset.avedonGridVariantId;
        const state = results[variantId];
        if (state) renderEditionCount(el, { tracked: true, ...state });
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', initAvedonScarcity);
